import { Box, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { Page } from "@mb-pro-ui/components";
import {
  EnhancedTable,
  FilterView as TableFilterView,
  Filter as TableFilter,
  SimpleBooleanFilter,
  SimpleTextFilter,
  RemoteFilterViews,
} from "@mb-pro-ui/components/table";
import { Company, UserDataExtended, UserFilter } from "../utils/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CellProps, Column, Row } from "react-table";
import { defineMessage, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";

import { useErrorHandler } from "../hooks/useErrorHandler";
import { useMutation } from "react-query";
import { useApi } from "../utils/ApiProvider";
import { usePageSxProps } from "./styles";
import { isEqual } from "lodash-es";
import { isAfter } from "date-fns";
import { APP_NAME } from "../App";

export const UsersPage = () => {
  const [filter, setFilter] = useState<UserFilter>({});

  const { listPageSx } = usePageSxProps();
  const { formatMessage } = useIntl();
  const history = useHistory();
  const api = useApi();
  const theme = useTheme();

  // TODO: többi előfordulható hiba
  const { errorHandler: onError } = useErrorHandler([
    [
      { status: 401, type: "user-admin" },
      () => formatMessage({ defaultMessage: "You have no rights for user management." }),
      () => {
        history.push("/");
      },
    ],
  ]);

  const listQuery = useCallback(
    (filter: UserFilter) =>
      api<UserDataExtended[]>("/users/list", {
        method: "post",
        body: JSON.stringify(filter),
      }),
    [api]
  );
  const { mutate: getList, data: list, status: queryStatus } = useMutation({ mutationFn: listQuery, onError });

  const companiesQuery = useCallback(() => api<Company[]>("/companies"), [api]);
  const { mutate: getCompanies, data: companies } = useMutation({ mutationFn: companiesQuery, onError });
  const companyMap = useMemo<Map<number, string> | null>(() => {
    if (!companies) {
      return null;
    }
    const map = new Map<number, string>();
    companies.forEach(({ id, name }) => {
      map.set(id, name);
    });

    return map;
  }, [companies]);

  const columns = useMemo(
    (): Column<UserDataExtended>[] => [
      {
        id: "name",
        Header: formatMessage({
          defaultMessage: "Username",
        }),
        accessor: (item) => item.name,
        width: 50,
      },
      {
        id: "fullname",
        Header: formatMessage({
          defaultMessage: "Name",
        }),
        accessor: (item) => item.fullname,
        width: 50,
      },
      {
        id: "email",
        Header: formatMessage({
          defaultMessage: "E-mail",
        }),
        accessor: (item) => item.email,
        width: 75,
      },
      {
        id: "companies",
        Header: formatMessage({
          defaultMessage: "Companies",
        }),
        accessor: (item) =>
          item.companies === null
            ? ""
            : item.companies.includes(-1)
            ? formatMessage({ defaultMessage: "Global company access" })
            : companyMap
            ? item.companies.map((cId) => companyMap.get(cId)).join(", ")
            : "...",
        Cell: ({ row }: CellProps<UserDataExtended>) => (
          <Box sx={{ height: 24, minHeight: 24, maxWidth: 1 / 1, overflow: "hidden" }}>
            <Typography component={"span"} sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>
              {row.values.companies}
            </Typography>
          </Box>
        ),
        width: 100,
      },
      {
        id: "unitPermissions",
        Header: formatMessage({
          defaultMessage: "Unit permissions",
        }),
        accessor: (item) =>
          item.unit_permissions
            ?.filter(({ expires }) => expires === null || isAfter(new Date(expires), new Date()))
            .map(({ unitid }) => unitid)
            .join(", "),
        Cell: ({ row }: CellProps<UserDataExtended>) => (
          <Box sx={{ height: 24, minHeight: 24, maxWidth: 1 / 1, overflow: "hidden" }}>
            <Typography component={"span"} sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>
              {row.values.unitPermissions}
            </Typography>
          </Box>
        ),
        width: 75,
      },
    ],
    [companyMap, formatMessage]
  );

  const tableFilters = useMemo(
    (): TableFilterView[] => [
      {
        id: "name",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Username" }) }),
      },
      {
        id: "fullname",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "Name" }) }),
      },
      {
        id: "email",
        Filter: SimpleTextFilter({ label: formatMessage({ defaultMessage: "E-mail" }) }),
      },
      {
        id: "include-inactive",
        Filter: SimpleBooleanFilter(defineMessage({ defaultMessage: "Incule inactive users" }), null, "hideFalse"),
      },
    ],
    [formatMessage]
  );

  const filterChanged = useCallback((filter: TableFilter[]) => {
    setFilter((old) => {
      const newFilter = {
        active: true,
      } as UserFilter;
      filter.forEach(({ id, value }) => {
        if (id === "name" && value) {
          newFilter.name = value;
        }
        if (id === "fullname" && value) {
          newFilter.fullname = value;
        }
        if (id === "email" && value) {
          newFilter.email = value;
        }
        if (id === "include-inactive" && value) {
          delete newFilter.active;
        }
      });
      return isEqual(old, newFilter) ? old : newFilter;
    });
  }, []);

  const getStyle = useCallback(
    (item: UserDataExtended) => {
      const color = theme.palette.common.black;
      const backgroundColor = item.active ? theme.palette.common.white : theme.palette.grey["100"];
      const fontStyle = item.active ? "normal" : "italic";

      return { color, backgroundColor, fontStyle };
    },
    [theme]
  );

  const rowProps = useCallback(
    (row: Row<UserDataExtended>) => ({
      style: {
        ...getStyle(row.original),
      },
    }),
    [getStyle]
  );

  const newUser = useCallback(() => {
    history.push("/users/new");
  }, [history]);

  const updateUsers = useCallback(() => getList(filter), [filter, getList]);

  useEffect(() => {
    document.title = `${formatMessage({ defaultMessage: "Users" })} - ${APP_NAME}`;
  }, [formatMessage]);

  useEffect(() => {
    updateUsers();
  }, [updateUsers]);

  useEffect(() => {
    getCompanies();
  }, [getCompanies]);

  return (
    <Page sx={listPageSx}>
      {/* TODO: filter */}
      <EnhancedTable
        columns={columns}
        data={list ?? []}
        showResultCount
        getRowProps={rowProps}
        queryStatus={queryStatus}
        overscanCount={5}
        onRowClick={(item: Row<UserDataExtended>) => history.push(`/users/${item.original.id}`)}
        postfix={
          <>
            <IconButton
              size="small"
              sx={{
                color: "primary.contrastText",
              }}
              onClick={updateUsers}
            >
              <Tooltip
                title={formatMessage({
                  defaultMessage: "Reload",
                })}
              >
                <RefreshIcon />
              </Tooltip>
            </IconButton>
            <IconButton
              size="small"
              sx={{
                color: "primary.contrastText",
              }}
              onClick={newUser}
            >
              <Tooltip
                title={formatMessage({
                  defaultMessage: "Add new user",
                })}
              >
                <AddIcon />
              </Tooltip>
            </IconButton>
          </>
        }
        filters={(opts) => <RemoteFilterViews {...opts} tableFilters={tableFilters} onFilterChange={filterChanged} />}
      />
    </Page>
  );
};

export default UsersPage;
